import { ElementType, LayoutElement } from '../types';
export { DUAL_RAIL_LIFT } from '../components/canvas/DualRailTrack3D';

/** Default mm per canvas unit when no floor plan scale is available. */
export const DEFAULT_MM_PER_CANVAS_UNIT = 20;

const MM_PER_M = 1000;

/** Default extrusion depth (Three.js Y) per component class. */
const DEFAULT_DEPTH: Partial<Record<ElementType, number>> = {
  // Vertical rail profile height (Three.js Y).
  'overhead-rail': 3,
  'overhead-curve': 3,
  'cross-rail': 3,
  'oht-vehicle': 8,
  stocker: 30,
  stb: 30,
  ohb: 50,
  lifter: 80,
  'support-pillar': 160,
  'horizontal-beam': 3,
  'suspension-rod': 155,
  rectangle: 8,
  circle: 8,
  text: 4,
};

/**
 * Non-stocker equipment types with a mandatory fixed 3D extrusion depth.
 */
const EQUIPMENT_TYPES_FIXED_DEPTH: ReadonlySet<ElementType> = new Set([
  'stb',
  'ohb',
  'lifter',
]);

/** Mandatory 3D Z extrusion depth for non-stocker equipment. */
export const EQUIPMENT_3D_DEPTH = 10;

/** Mandatory 3D Z extrusion depth for stockers. */
export const STOCKER_3D_DEPTH = 30;

export function isFixedEquipment3DDepth(type: ElementType): boolean {
  return EQUIPMENT_TYPES_FIXED_DEPTH.has(type) || type === 'stocker';
}

/** Layout rectangles / floor units created from DXF or marked equipment overlays. */
export function isDxfEquipmentLayoutElement(element: LayoutElement): boolean {
  if (element.type === 'stocker' || element.type === 'oht-vehicle') return false;
  const props = element.customProperties || {};
  if (props.objectType === 'stocker') return false;

  const floorType =
    element.type === 'rectangle' ||
    element.type === 'stb' ||
    element.type === 'ohb' ||
    element.type === 'lifter';
  if (!floorType) return false;

  if (props.source === 'dxf-equipment' || props.source === 'dxf-insert') return true;
  if (
    props.objectType === 'equipment' ||
    props.objectType === 'buffer' ||
    props.objectType === 'lift' ||
    props.objectType === 'other' ||
    props.objectType === 'utility'
  ) {
    return true;
  }
  if (typeof props.dxfEquipmentId === 'string' && props.dxfEquipmentId.length > 0) return true;
  if (typeof props.processType === 'string' && props.processType.trim()) return true;
  if (typeof props.equipmentType === 'string' && props.equipmentType.trim()) return true;
  return false;
}

/** True for stockers and fab equipment that should always paint from layout elements. */
export function isLayoutEquipmentElement(element: LayoutElement): boolean {
  if (element.type === 'stocker' || element.type === 'stb' || element.type === 'ohb' || element.type === 'lifter') {
    return true;
  }
  return isDxfEquipmentLayoutElement(element);
}

/** Global configuration settings for 3D tracks (overhead-rail, overhead-curve, cross-rail). */
/** Horizontal track width when thicknessMeters is missing — matches 2D canvas fallback. */
export const TRACK_3D_THICKNESS = 4;
export const TRACK_3D_ELEVATION = 30; // Height (elevation) of the 3D tracks above the floor by default
/** Minimum cross-section so rails stay visible at fab / DXF scales. */
export const TRACK_3D_MIN_THICKNESS = 2;
export const TEXTURE_PATH = '/textures/metal_brushed.svg';

export const RAIL_MODEL_OBJ_PATH = '/materials/rail.obj?v=1';
export const RAIL_MODEL_MTL_PATH = '/materials/rail.mtl?v=1';

/** Global configuration settings for OHT vehicle models. */
export const OHT_MODEL_PATH = '/materials/britelab_oht.obj?v=20';
export const OHT_MODEL_MTL_PATH = '/materials/britelab_oht.mtl?v=20';

/** Global configuration settings for stocker models. */
export const STOCKER_MODEL_OBJ_PATH = '/materials/fabStocker.obj?v=8';
export const STOCKER_MODEL_MTL_PATH = '/materials/fab_stocker.mtl?v=6';
/** Shared industrial wall panel for extruded DXF wall layers. */
export const INDUSTRIAL_WALL_MODEL_OBJ_PATH = '/materials/Industrial_Wall.obj?v=1';

/** Shared equipment OBJ for non-stocker floor equipment. */
export const FAB_EQUIPMENT_MODEL_OBJ_PATH = '/materials/fab_Equipment.obj?v=2';
export const OHT_MODEL_SCALE_MULTIPLIER = 0.65; // Scale multiplier for the OHT 3D model (e.g. 0.65 for scaled down model)
export const OHT_MODEL_Y_OFFSET = 0.0; // Vertical offset to align OHT wheels with the track

export const DEFAULT_RAIL_HEIGHT_M = 3.2;

/** Types whose `position.z` is vertical elevation in 3D (not extrusion depth). */
const ELEVATED_3D_TYPES: ReadonlySet<ElementType> = new Set(['overhead-rail', 'overhead-curve', 'cross-rail', 'oht-vehicle']);

/**
 * Check if element type uses 3D elevation (position.z as vertical height).
 * @param type - Element type to check
 * @returns true if type is an elevated 3D element (overhead-rail, overhead-curve, cross-rail, oht-vehicle)
 */
export function elementUses3DElevation(type: ElementType): boolean {
  return ELEVATED_3D_TYPES.has(type);
}

/**
 * Convert rail height in metres to canvas elevation (position.z / Three.js Y).
 * Uses floor-plan scale when available: canvasUnits = (metres × 1000) / mmPerUnit.
 */
export function heightMToCanvasElevation(
  heightM: unknown,
  mmPerCanvasUnit: number = DEFAULT_MM_PER_CANVAS_UNIT
): number {
  const metres =
    typeof heightM === 'number' ? heightM : parseFloat(String(heightM ?? DEFAULT_RAIL_HEIGHT_M));
  if (!Number.isFinite(metres) || mmPerCanvasUnit <= 0) {
    return (DEFAULT_RAIL_HEIGHT_M * MM_PER_M) / DEFAULT_MM_PER_CANVAS_UNIT;
  }
  return (metres * MM_PER_M) / mmPerCanvasUnit;
}

/**
 * Compute default 3D elevation (position.z / Three.js Y) for an element type.
 * Elevated types (rails, vehicles) return TRACK_3D_ELEVATION; floor objects return 0.
 * @param type - Element type
 * @param _customProperties - Reserved for future use
 * @returns Default Z elevation in canvas units
 */
export function defaultElementZ(
  type: ElementType,
  _customProperties?: Record<string, unknown>
): number {
  if (elementUses3DElevation(type)) {
    return TRACK_3D_ELEVATION; // match FactoryFloor3D yOffset for elevated elements
  }
  return 0;
}

/** Minimum visible extrusion when z/depth is zero. */
export const MIN_3D_EXTRUSION_DEPTH = 2;

/**
 * Get the vertical 3D elevation (position.z) for an element.
 * Uses explicit position.z if present; otherwise applies type-specific defaults.
 * For elevated types (rails), caches default of 25 are normalized to TRACK_3D_ELEVATION.
 * @param element - Layout element
 * @param mmPerCanvasUnit - Scale factor for unit conversion (default: 20mm/unit)
 * @returns Elevation in canvas units
 */
export function getElementElevationZ(
  element: LayoutElement
): number {
  if (elementUses3DElevation(element.type)) {
    // Handle unassigned or old default elevation (1 or 25) in existing layouts
    if (
      element.position.z === undefined ||
      element.position.z === null ||
      element.position.z === 1 ||
      element.position.z === 25
    ) {
      return TRACK_3D_ELEVATION;
    }
    return element.position.z;
  }
  if (element.position.z !== undefined && element.position.z !== null) {
    return element.position.z;
  }
  return defaultElementZ(element.type, element.customProperties);
}

/**
 * Get the Y coordinate for a 3D group in Three.js scene.
 * Elevated types (rails, OHT) use position.z as vertical Y; floor objects are grounded at Y=0.
 * @param element - Layout element
 * @param mmPerCanvasUnit - Scale factor for unit conversion (default: 20mm/unit)
 * @returns Three.js Y coordinate (vertical position)
 */
export function getElement3DGroupY(
  element: LayoutElement,
  _mmPerCanvasUnit?: number,
  railElevation?: number
): number {
  if (elementUses3DElevation(element.type)) {
    const z = getElementElevationZ(element);
    // Rails and the vehicles hanging off them share one plane, so a scene-wide
    // elevation may only lift them — never drop a rail the user placed higher.
    return railElevation === undefined ? z : Math.max(z, railElevation);
  }
  return 0;
}

/** Gap kept between the tallest tool and the rail underside. */
export const RAIL_TOOL_CLEARANCE = 6;

/** Floor types that sit under the rail and therefore have to be cleared. */
const RAIL_CLEARANCE_TYPES: ReadonlySet<ElementType> = new Set([
  'stocker',
  'stb',
  'ohb',
  'lifter',
  'rectangle',
]);

/**
 * Default process-tool cabinet size for new drops / fallbacks.
 * In 3D, width/depth follow the drawn layout footprint when present so tools
 * keep the same XY spacing as 2D (only extrusion height uses the kit default).
 */
export const EQUIPMENT_KIT_WIDTH = 80;
/** Depth of the 2D footprint (toward the rail) for a newly drawn tool. */
export const EQUIPMENT_KIT_DEPTH = 48;
export const EQUIPMENT_KIT_HEIGHT = 24;

/**
 * Kit height the AMHS equipment adapter builds a tool at.
 *
 * Lives here rather than beside the adapter so the rail can be lifted clear of
 * the tools without a 3D component having to import from a React module.
 */
export function resolveEquipmentKitHeight(_w: number, _h: number, depth: number): number {
  return Math.max(EQUIPMENT_KIT_HEIGHT, depth);
}

/**
 * Vertical extent a tool is actually drawn at, whichever detail level draws it.
 *
 * The full process-tool kit floors short tools at `EQUIPMENT_KIT_HEIGHT`, while
 * the stocker rack uses its extrusion as-is. Both the detailed kits and the
 * instanced overview silhouette must resolve height through here, or a tool
 * changes size as it crosses the detail threshold.
 */
export function resolveEquipmentRenderHeight(
  kind: 'equipment' | 'stocker',
  width: number,
  height: number,
  extrusionDepth: number
): number {
  return kind === 'stocker'
    ? extrusionDepth
    : resolveEquipmentKitHeight(width, height, extrusionDepth);
}

/**
 * Rail plane for a whole layout: high enough to clear the tallest tool under it.
 *
 * `TRACK_3D_ELEVATION` alone is 30; a tool with an explicit extrusion deeper
 * than that still has to be cleared.
 */
export function resolveRailElevation(elements: Iterable<LayoutElement>): number {
  let tallest = 0;
  for (const element of elements) {
    if (!RAIL_CLEARANCE_TYPES.has(element.type)) continue;
    const { width, height } = element.dimensions;
    tallest = Math.max(
      tallest,
      resolveEquipmentKitHeight(width, height, getElement3DExtrusionDepth(element))
    );
  }
  return Math.max(TRACK_3D_ELEVATION, tallest + RAIL_TOOL_CLEARANCE);
}

/**
 * Get the extrusion depth (Three.js Y scale) for a 3D element.
 * Prefers dimensions.depth; falls back to type defaults or legacy position.z for floor objects.
 * Note: For elevated types, position.z is elevation, not extrusion depth.
 * Non-stocker equipment always uses EQUIPMENT_3D_DEPTH (mandatory).
 * Stockers always use STOCKER_3D_DEPTH (mandatory).
 * @param element - Layout element
 * @returns Extrusion depth in canvas units (minimum: MIN_3D_EXTRUSION_DEPTH)
 */
export function getElement3DExtrusionDepth(element: LayoutElement): number {
  if (element.type === 'stocker') {
    return 30;
  }

  if (elementUses3DElevation(element.type)) {
    const typeDefault = DEFAULT_DEPTH[element.type] ?? MIN_3D_EXTRUSION_DEPTH;
    if (element.dimensions.depth !== undefined && element.dimensions.depth > 0) {
      // Rails and OHT vehicles always use their fixed profile height (ignore legacy/generated thick depths).
      if (
        element.type === 'overhead-rail' ||
        element.type === 'overhead-curve' ||
        element.type === 'cross-rail' ||
        element.type === 'oht-vehicle'
      ) {
        return typeDefault;
      }
      return element.dimensions.depth;
    }
    return typeDefault;
  }

  if (element.position.z !== undefined && element.position.z !== null) {
    return Math.max(0.5, element.position.z);
  }

  if (element.dimensions.depth !== undefined && element.dimensions.depth > 0) {
    return element.dimensions.depth;
  }

  return DEFAULT_DEPTH[element.type] ?? MIN_3D_EXTRUSION_DEPTH;
}

/**
 * Get default extrusion depth for a type when placing new elements.
 * @param type - Element type
 * @returns Default depth in canvas units
 */
export function defaultElementDepth(type: ElementType): number {
  if (type === 'stocker') {
    return STOCKER_3D_DEPTH;
  }
  if (EQUIPMENT_TYPES_FIXED_DEPTH.has(type)) {
    return EQUIPMENT_3D_DEPTH;
  }
  return DEFAULT_DEPTH[type] ?? MIN_3D_EXTRUSION_DEPTH;
}

export type Apply3DDefaultsOptions = {
  /** Skip rail elevation — render as flat plan (top-down camera view). */
  flatPlan?: boolean;
};

/**
 * Apply 3D defaults during element import.
 * Sets elevation (z) for overhead rails from heightM, depth for all types, and ground-level z for floor equipment.
 * @param element - Layout element to process
 * @param mmPerCanvasUnit - Scale factor for unit conversion (default: 20mm/unit)
 * @param options - Optional flags (flatPlan: render rails as flat instead of elevated)
 * @returns Element with 3D defaults applied
 */
export function apply3DDefaultsOnImport(
  element: LayoutElement,
  _mmPerCanvasUnit?: number,
  options?: Apply3DDefaultsOptions
): LayoutElement {
  const type = element.type;
  const custom = element.customProperties ?? {};
  const defaultDepth = defaultElementDepth(type);
  const flatPlan = options?.flatPlan === true;

  let z = element.position.z;
  if (z === undefined || z === null) {
    z = defaultElementZ(type, custom);
  }

  const depth = flatPlan
    ? type === 'overhead-rail'
      ? 1
      : 2
    : type === 'stocker'
      ? 30
      : element.dimensions.depth !== undefined && element.dimensions.depth > 0
        ? element.dimensions.depth
        : defaultDepth;

  return {
    ...element,
    position: { ...element.position, z },
    dimensions: { ...element.dimensions, depth },
  };
}

/**
 * Apply 3D defaults to all elements in a collection.
 * @param elements - Record of layout elements indexed by ID
 * @param mmPerCanvasUnit - Scale factor for unit conversion (default: 20mm/unit)
 * @param options - Optional flags
 * @returns New record with 3D defaults applied to all elements
 */
export function apply3DDefaultsToElements(
  elements: Record<string, LayoutElement>,
  mmPerCanvasUnit: number = DEFAULT_MM_PER_CANVAS_UNIT,
  options?: Apply3DDefaultsOptions
): Record<string, LayoutElement> {
  const out: Record<string, LayoutElement> = {};
  for (const [id, el] of Object.entries(elements)) {
    out[id] = apply3DDefaultsOnImport(el, mmPerCanvasUnit, options);
  }
  return out;
}
